const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

const noop = (key, fallback) => fallback !== undefined ? fallback : key;

function shortDay(d, t) {
  return t(`map.days.${d.toLowerCase()}`, d.slice(0, 3));
}

function fmtTime(t) {
  if (!t) return '';
  // Convert "HH:MM" to "h:mm am/pm"
  const [hh, mm] = t.split(':').map(Number);
  if (isNaN(hh)) return t;
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  const ampm = hh < 12 ? 'am' : 'pm';
  return `${h12}:${String(mm).padStart(2,'0')}${ampm}`; // Removed space before am/pm for Map space
}

export function summarizeBusinessHours(bh = {}, t = noop) {
  // 1. Priority Flags
  if (bh?.isOpen247) return t('map.hours.open247', 'Open 24/7');

  // 2. Variable Hours Logic
  // If variable is true AND no specific days are set, return just "Variable Hours"
  const hasNoDaysSet = !DAYS.some(d => bh[d]?.isOpen);
  if (bh?.variableHours && hasNoDaysSet) return t('map.hours.variable', 'Variable Hours');

  // 3. Grouping Logic
  const groups = [];
  let i = 0;
  while (i < DAYS.length) {
    const day = DAYS[i];
    const cur = bh[day] || { isOpen: false };
    let j = i + 1;

    while (j < DAYS.length) {
      const next = bh[DAYS[j]] || { isOpen: false };
      const same = (!!cur.isOpen === !!next.isOpen) &&
                   (cur.opening === next.opening) &&
                   (cur.closing === next.closing);
      if (!same) break;
      j++;
    }

    if (cur.isOpen) {
      groups.push({
        start: i,
        end: j-1,
        opening: cur.opening,
        closing: cur.closing
      });
    }
    i = j;
  }

  const openGroups = groups;
  if (openGroups.length === 0) return t('map.hours.closed', 'Closed');

  const tokens = openGroups.map(g => {
    const dayLabel = g.start === g.end
      ? shortDay(DAYS[g.start], t)
      : `${shortDay(DAYS[g.start], t)}–${shortDay(DAYS[g.end], t)}`;

    const timeLabel = (g.opening && g.closing)
      ? `${fmtTime(g.opening)}–${fmtTime(g.closing)}`
      : t('map.hours.open', 'Open');

    return `${dayLabel} ${timeLabel}`;
  });

  let result = tokens.join(', ');

  // If Variable is checked but we have hours, add a small indicator
  if (bh?.variableHours) result = `~ ${result}`;

  return result;
}

export function perDayBusinessHours(bh = {}, t = noop) {
  // If 24/7, all days show 24h
  if (bh?.isOpen247) {
    return DAYS.map(d => ({ day: d, label: t('map.hours.open24h', 'Open 24h') }));
  }

  return DAYS.map(d => {
    const v = bh[d] || { isOpen: false };

    let label = t('map.hours.closed', 'Closed');
    if (v.isOpen) {
      label = (v.opening && v.closing)
        ? `${fmtTime(v.opening)}–${fmtTime(v.closing)}`
        : t('map.hours.open', 'Open');

      // If variable is on, add a tiny indicator to the time
      if (bh.variableHours) label += ' *';
    }

    return { day: d, label };
  });
}
